import { resolveBuildAnswerRevealMode } from '@/shared/utils/answerRevealMode';
import { type QuizSet } from '@/types';

import { type BuildSchemaInputType } from '../form/schema';

import { adaptQuizSetDataToBuildSchema } from './apiAdapter';

interface AiTutorInfo {
  id: string;
  name: string;
  previewAvatarSrc: string;
  introduction: string;
}

interface BuildDefaultValuesOptions {
  updateData?: QuizSet;
  actionType: 'CREATE' | 'UPDATE' | 'DUPLICATE';
  schoolInfo: Partial<BuildSchemaInputType>;
  aiTutor?: AiTutorInfo;
}

/**
 * Build default form values for the build page based on schema structure
 */
export function buildDefaultFormValues(
  options: BuildDefaultValuesOptions,
): BuildSchemaInputType {
  const { updateData, actionType, schoolInfo, aiTutor } = options;

  return {
    // Basic info
    ...buildBasicInfoDefaults(updateData, actionType),

    // Quiz options
    ...buildQuizOptionsDefaults(updateData),

    // Quiz list
    quizList: buildQuizListDefaults(updateData),

    // School info (includes subject and other required fields)
    subject: schoolInfo.subject || '',
    subSubject: schoolInfo.subSubject || '',
    schoolTypeId: schoolInfo.schoolTypeId || '',
    ...schoolInfo,
    isLastSchoolInfoSaved: true,

    // Materials
    materials: buildMaterialsDefaults(updateData),
    isLearningMaterialEnabled: !!updateData?.materials?.length,

    // AI settings
    ...buildAiSettingsDefaults(updateData, aiTutor),
  };
}

/**
 * Build basic info defaults (id, title, template)
 */
function buildBasicInfoDefaults(
  updateData: QuizSet | undefined,
  actionType: 'CREATE' | 'UPDATE' | 'DUPLICATE',
): Pick<BuildSchemaInputType, 'id' | 'title' | 'templateId' | 'templateType'> {
  return {
    id: updateData?.id ?? undefined,
    title: getTitle(updateData, actionType),
    templateId: updateData?.templateId ?? '',
    templateType: updateData?.templateType ?? '',
  };
}

/**
 * Get title based on action type
 */
function getTitle(
  updateData: QuizSet | undefined,
  actionType: 'CREATE' | 'UPDATE' | 'DUPLICATE',
): string {
  if (actionType === 'CREATE') {
    return '';
  }
  return updateData?.title || '';
}

/**
 * Build quiz options defaults
 */
function buildQuizOptionsDefaults(
  updateData: QuizSet | undefined,
): Pick<
  BuildSchemaInputType,
  | 'isPublic'
  | 'isAllowPass'
  | 'answerRevealMode'
  | 'isNextQuizsetAvailable'
  | 'nextQuizsetId'
> {
  const answerRevealMode = resolveBuildAnswerRevealMode({
    answerRevealMode: updateData?.answerRevealMode,
    showIncorrectAnswer: updateData?.showIncorrectAnswer,
    templateType: updateData?.templateType,
  });

  return {
    isPublic: updateData?.isPublic ?? true,
    isAllowPass: updateData?.isAllowPass ?? true,
    answerRevealMode,
    isNextQuizsetAvailable: updateData?.isNextQuizsetAvailable ?? false,
    nextQuizsetId: updateData?.nextQuizsetId ?? undefined,
  };
}

function buildQuizListDefaults(
  updateData: QuizSet | undefined,
): BuildSchemaInputType['quizList'] {
  if (!updateData?.quizList || updateData.quizList.length === 0) {
    return [];
  }

  const adapted = adaptQuizSetDataToBuildSchema(updateData.quizList);

  if (adapted.length === 0) return [];

  return adapted;
}

/**
 * Build materials defaults
 */
function buildMaterialsDefaults(
  updateData: QuizSet | undefined,
): BuildSchemaInputType['materials'] {
  return (
    updateData?.materials.map(material => ({
      ...material,
      fileSize: material.fileSize ?? 0,
    })) ?? []
  );
}

/**
 * Build AI settings defaults
 */
function buildAiSettingsDefaults(
  updateData: QuizSet | undefined,
  aiTutor?: AiTutorInfo,
): Pick<
  BuildSchemaInputType,
  | 'aiTranslationLanguages'
  | 'isAiTranslationEnabled'
  | 'aiTutorEnabled'
  | 'aiTutor'
> {
  return {
    aiTranslationLanguages: updateData?.aiTranslationLanguages ?? [],
    isAiTranslationEnabled: updateData?.isAiTranslationEnabled ?? false,
    aiTutorEnabled: !!updateData?.aiTutorEnabled,
    aiTutor: buildAiTutorDefaults(aiTutor),
  };
}

/**
 * Build AI tutor defaults
 */
function buildAiTutorDefaults(
  aiTutor?: AiTutorInfo,
): BuildSchemaInputType['aiTutor'] {
  if (!aiTutor) {
    return undefined;
  }

  return {
    id: aiTutor.id,
    name: aiTutor.name,
    avatarSrc: aiTutor.previewAvatarSrc,
    introduction: aiTutor.introduction,
  };
}
